import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, FileWarning, Wrench, Siren, ClipboardCheck, Flame } from "lucide-react";
import { api, MODULE_PATHS } from "../lib/api";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { RagBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";

const BIG = { red: "border-red-500/40 text-red-400", amber: "border-amber-500/40 text-amber-400", green: "border-emerald-500/40 text-emerald-400" };
const BIG_LABEL = { red: "Red - overdue / expired", amber: "Amber - due within 30 days", green: "Green - compliant" };

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="panel p-4" data-testid={testid}>
    <div className="flex items-center justify-between"><span className="eyebrow">{label}</span><Icon className="h-4 w-4 text-slate-500" /></div>
    <p className="mt-2 font-mono text-3xl font-bold">{value}</p>
  </div>
);

const ItemList = ({ title, items, status, testid }) => (
  <div className="panel" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3"><h3 className="font-display text-lg font-bold uppercase">{title}</h3><RagBadge status={status} label={`${items.length}`} /></div>
    <div className="max-h-[420px] divide-y divide-slate-800 overflow-y-auto">
      {items.map((i, n) => (
        <Link key={n} to={i.link || "/"} className="block px-4 py-3 hover:bg-slate-800/40">
          <p className="text-sm text-slate-100">{i.label}</p>
          <p className="text-xs text-slate-400"><span className="font-mono uppercase text-slate-500">{i.module}</span> - {i.detail}</p>
        </Link>
      ))}
      {!items.length && <p className="px-4 py-8 text-center text-sm text-slate-500">Nothing here.</p>}
    </div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { site } = useSite();
  const { can } = useAuth();
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);
  if (!d) return <p className="text-slate-500" data-testid="dashboard-loading">Loading compliance data...</p>;
  const k = d.kpis;
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={`Site dashboard - ${site?.type || "Site"}`} title={site?.name || "Site compliance overview"} description="Live RAG status across fire, COSHH, PUWER, welfare, first aid, ladders, maintenance and incident CAPA." />
      <div className="fade-up grid gap-4 md:grid-cols-3">
        {["red", "amber", "green"].map((s) => (
          <div key={s} className={`panel border-l-4 p-5 ${BIG[s]}`} data-testid={`rag-total-${s}`}>
            <p className="eyebrow">{BIG_LABEL[s]}</p>
            <p className="mt-2 font-mono text-5xl font-black">{d.totals[s]}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Kpi icon={AlertTriangle} label="Overdue actions" value={d.categories.overdue_actions} testid="cat-overdue-actions" />
        <Kpi icon={Clock} label="Overdue inspections" value={d.categories.overdue_inspections} testid="cat-overdue-inspections" />
        <Kpi icon={FileWarning} label="Expired certificates" value={d.categories.expired_certificates} testid="cat-expired-certificates" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Wrench} label="Open work orders" value={k.open_work_orders} testid="kpi-open-wo" />
        <Kpi icon={Flame} label="High risk open" value={k.high_risk_open} testid="kpi-high-risk" />
        <Kpi icon={Siren} label="Open incidents" value={k.open_incidents} testid="kpi-open-incidents" />
        <Kpi icon={ClipboardCheck} label="Inspections (30d)" value={k.inspections_30d} testid="kpi-inspections" />
      </div>
      <h2 className="font-display mb-3 mt-8 text-2xl font-bold uppercase">Compliance by module</h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {d.modules.filter((m) => can(m.key, "view")).map((m) => (
          <Link key={m.key} to={MODULE_PATHS[m.key]} className="panel p-4 transition-colors hover:border-slate-600" data-testid={`module-card-${m.key}`}>
            <div className="flex items-center justify-between"><p className="font-semibold">{m.name}</p><RagBadge status={m.status} /></div>
            <p className="mt-3 font-mono text-3xl font-bold">{m.score}%</p>
            <div className="mt-2 flex h-1.5 overflow-hidden rounded bg-slate-800">
              <div className="bg-red-500" style={{ width: `${(m.red / (m.total || 1)) * 100}%` }} />
              <div className="bg-amber-500" style={{ width: `${(m.amber / (m.total || 1)) * 100}%` }} />
              <div className="bg-emerald-500" style={{ width: `${(m.green / (m.total || 1)) * 100}%` }} />
            </div>
            <p className="mt-2 font-mono text-[11px] text-slate-400">{m.red} red - {m.amber} amber - {m.green} green</p>
          </Link>
        ))}
      </div>
      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <ItemList title="Red - action now" items={d.red_items} status="red" testid="red-items-list" />
        <ItemList title="Amber - due within 30 days" items={d.amber_items} status="amber" testid="amber-items-list" />
      </div>
    </div>
  );
}
